'use client';

import * as React from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { AnalyticsProvider as AnalyticsProviderImpl } from '../provider';
import type { AnalyticsProvider as AnalyticsProviderInterface } from '../provider/types';

function useTrackPageView(trackInitialPageView: boolean): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstPageView = React.useRef(true);

  React.useEffect(() => {
    // By default the first pageview is skipped - PostHogUserIdentifier sends it
    // after identify(). Apps without an identifier (marketing) must opt in via
    // trackInitialPageView or bounce sessions are never recorded.
    if (isFirstPageView.current) {
      isFirstPageView.current = false;
      if (!trackInitialPageView) return;
    }
    const url = [pathname, searchParams.toString()].filter(Boolean).join('?');
    AnalyticsProviderImpl.trackPageView(url);
  }, [pathname, searchParams, trackInitialPageView]);
}

export const AnalyticsContext = React.createContext<AnalyticsProviderInterface>(
  AnalyticsProviderImpl
);

export function AnalyticsProvider(
  props: React.PropsWithChildren<{ trackInitialPageView?: boolean }>
) {
  if (typeof window === 'undefined') {
    return props.children;
  }
  useTrackPageView(props.trackInitialPageView ?? false);
  return (
    <AnalyticsContext.Provider value={AnalyticsProviderImpl}>
      {props.children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsProviderInterface {
  return React.useContext(AnalyticsContext);
}
